import { useMemo } from "react"

import { buildLicenseCode } from "../lib/buildLicenseCode"
import { generateLicenseMarkdown } from "../lib/generateLicenseMarkdown"
import { getLicenseLogos, type LicenseLogo } from "../lib/getLicenseLogos"
import {
  normalizeLicenseData,
  type NormalizedLicenseData
} from "../lib/normalizeLicenseData"
import type { LicenseIssue } from "../lib/licenseValidation"
import { useLicenseStore, type LicenseData } from "./useLicenseStore"

type LicenseDataSnapshot = {
  rawData: LicenseData
  normalizedData: NormalizedLicenseData
  issues: LicenseIssue[]
  markdown: string
  exportMarkdown: string
  logos: LicenseLogo[]
  licenseCode: string
  licenseName: string
  licenseTermsCode: string
  licenseFullCode: string
}

export const useLicenseData = (): LicenseDataSnapshot => {
  const rawData = useLicenseStore(state => state.data)

  const { normalizedData, issues } = useMemo(
    () => normalizeLicenseData(rawData),
    [rawData]
  )

  const licenseCode = useMemo(
    () => buildLicenseCode(normalizedData, "sharing"),
    [normalizedData]
  )

  const licenseTermsCode = useMemo(
    () => buildLicenseCode(normalizedData, "terms"),
    [normalizedData]
  )

  const licenseFullCode = useMemo(
    () => buildLicenseCode(normalizedData, "full"),
    [normalizedData]
  )

  // const licenseName = useMemo(
  //   () => (licenseCode && licenseCode !== "std" ? `Data Sharing License 1.0-${licenseCode}` : "Data Sharing License 1.0"),
  //   [licenseCode]
  // )
  const licenseName = 'Data Sharing License 1.0'

  const markdown = useMemo(
    () =>
      generateLicenseMarkdown(normalizedData, {
        licenseCode: licenseFullCode,
        subListStyle: "decimal",
        listStyle: "unordered"
      }),
    [licenseFullCode, normalizedData]
  )

  const exportMarkdown = useMemo(
    () => generateLicenseMarkdown(normalizedData, { licenseCode: licenseFullCode }),
    [licenseFullCode, normalizedData]
  )

  const logos = useMemo(
    () => getLicenseLogos(normalizedData),
    [normalizedData]
  )

  return {
    rawData,
    normalizedData,
    issues,
    markdown,
    exportMarkdown,
    logos,
    licenseCode,
    licenseName,
    licenseTermsCode,
    licenseFullCode
  }
}
